// ---------------------------------------------------------------------------
// V21 — TIMESHEETS AND APPROVAL.
//
// §V21: "Actual vs scheduled hours, worker-submitted or check-in-derived. Supervisor ->
// agency -> client approval chain with variance flags."
//
// ---------------------------------------------------------------------------
// ONE SCREEN, NOT TWO, AND THAT IS A DEPARTURE FROM THE PLAN
//
// `FORWARD-PLAN.md` asks for "a supervisor screen, an agency screen". They would read the
// same rows, in the same order, from the same call — the only difference is which button
// is live. `system/screens.ts` already carries V14's warning about exactly this: two
// screens over one set of bookings is how a product ends up with two answers to one
// question, and this would have been two screens over one set of hours.
//
// So there is one, and the affordance is per row and per step. The server says which
// steps this actor may take, and `0057`'s function refuses again on the other side — the
// button being visible was never the control.
//
// ---------------------------------------------------------------------------
// THE SHIFTS WITH NO TIMESHEET ARE THE POINT OF THE TABLE
//
// A supervisor's question on the morning after is "whose hours am I still waiting for",
// and a list of submitted timesheets cannot answer it. `app.event_timesheets` returns one
// row per booked shift whether or not anybody has claimed hours for it, so the gaps are
// visible rather than absent.
//
// ---------------------------------------------------------------------------
// NOTHING HERE COMPUTES AN HOUR
//
// Payable, scheduled and variance are generated columns read straight through. A minutes
// figure divided here would be a second arithmetic, and `13-…` §5's warning about two
// engines rounding differently applies to two engines SUBTRACTING differently just as
// much. What this file decides is colour, order of reading, and what to say when there is
// nothing to show.
//
// AND THE VARIANCE IS NEVER A COLOUR ALONE. Every row carries a chip with an icon and
// words, because this is read on a cracked phone at 06:00 by somebody who has been awake
// since four.
// ---------------------------------------------------------------------------

import { Fragment, useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { send } from "../api/auth";
import { console_ } from "../api/console";
import { LoadFailed } from "../design/LoadFailed";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import type { Failure } from "../system/failure";
import { typedTimeHint } from "../system/clock";
import { minutesAsSpan as hm, timesheetRowState as rowState, type TimesheetStep as Step } from "../system/timesheet";

type Row = {
  assignment_id: string;
  requirement_id: string | null;
  zone: string | null;
  role_name: string;
  worker_id: string;
  worker_name: string;
  scheduled_start: string;
  scheduled_end: string;
  assignment_status: string;
  is_reserve: boolean;
  timesheet_id: string | null;
  actual_start: string | null;
  actual_end: string | null;
  break_minutes: number | null;
  break_is_paid: boolean | null;
  units_produced: string | null;
  hours_from_presence: boolean | null;
  records_presence: boolean;
  payable_minutes: string | null;
  scheduled_minutes: string | null;
  variance_minutes: string | null;
  submitted_at: string | null;
  supersedes_id: string | null;
  correction_reason: string | null;
  steps_taken: string[];
  next_step: string | null;
  is_approved: boolean;
  variance_flagged: boolean;
};

type Summary = {
  shifts: number;
  submitted: number;
  approved: number;
  awaiting: number;
  with_variance: number;
};

type Payload = { steps: Step[]; rows: Row[]; summary: Summary };

type Reason = { id: string; label: string; description: string };

/** An instant as the value a `datetime-local` input takes, in this browser's clock. */
function localInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * SWEEP S4. HOURS COULD BE APPROVED AND NEVER RECORDED. `POST /v1/assignments/:id/timesheet`
 * had no caller, so pay, invoicing and the hours in a Proof Pack had nothing to start from.
 *
 * TWO WAYS IN, BECAUSE THE SHIFT'S CHECK-IN POLICY DECIDES WHICH IS ALLOWED, and only the
 * database reads that policy: hours taken from the check-in record send no times, stated
 * hours send both. The screen offers both and shows the database's own refusal when the
 * policy says the other — a second copy of that rule here would drift from `0057`.
 *
 * A CORRECTION SUPERSEDES; nothing is edited. It carries a reason code from the closed list.
 */
function HoursForm({
  row,
  reasons,
  onCancel,
  onDone,
}: {
  row: Row;
  reasons: Reason[];
  onCancel: () => void;
  onDone: () => Promise<void>;
}) {
  const { apiBase, me } = useSession();
  const { locale, t, refusal } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const correcting = row.timesheet_id !== null;
  const [start, setStart] = useState(localInput(row.actual_start ?? row.scheduled_start));
  const [end, setEnd] = useState(localInput(row.actual_end ?? row.scheduled_end));
  const [breakMinutes, setBreakMinutes] = useState(row.break_minutes === null ? "" : String(row.break_minutes));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(stated: boolean) {
    if (correcting && reason === "") {
      setError(t("Choose why these hours are being corrected."));
      return;
    }
    const payload: Record<string, unknown> = {};
    if (stated) {
      const a = new Date(start);
      const b = new Date(end);
      if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) {
        setError(t("Give both a start and an end."));
        return;
      }
      if (b <= a) {
        setError(t("The end must be after the start."));
        return;
      }
      payload.actual_start = a.toISOString();
      payload.actual_end = b.toISOString();
    }
    if (breakMinutes.trim() !== "") payload.break_minutes = Number(breakMinutes);
    if (correcting) {
      payload.supersedes = row.timesheet_id;
      payload.correction_reason = reason;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await send<{ error?: { message: string } }>(
        console_.submitTimesheet(apiBase, row.assignment_id, payload),
      );
      if (res.status >= 400) {
        setError(refusal(res.body?.error) ?? t("These hours could not be recorded."));
        return;
      }
      await onDone();
    } catch {
      setError(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <p className="muted">
        {t(correcting ? "Correct the hours for {name}." : "Record the hours for {name}.", { name: row.worker_name })}{" "}
        {t("Scheduled {start}–{end}.", { start: hhmm(row.scheduled_start), end: hhmm(row.scheduled_end) })}
      </p>
      <div className="row">
        <div className="field">
          <label htmlFor={`start-${row.assignment_id}`}>{t("Started")}</label>
          <input id={`start-${row.assignment_id}`} type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor={`end-${row.assignment_id}`}>{t("Finished")}</label>
          <input id={`end-${row.assignment_id}`} type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor={`break-${row.assignment_id}`}>{t("Break (minutes)")}</label>
          <input
            id={`break-${row.assignment_id}`}
            type="number"
            min={0}
            max={1440}
            inputMode="numeric"
            value={breakMinutes}
            onChange={(e) => setBreakMinutes(e.target.value)}
            placeholder={t("From the policy")}
          />
        </div>
        {correcting ? (
          <div className="field">
            <label htmlFor={`reason-${row.assignment_id}`}>{t("Why")}</label>
            <select id={`reason-${row.assignment_id}`} value={reason} onChange={(e) => setReason(e.target.value)}>
              <option value="">{t("Choose a reason")}</option>
              {reasons.map((r) => (
                <option key={r.id} value={r.id}>
                  {translateLabel(locale, r.label)}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>
      <p className="field-hint">{typedTimeHint(orgZone, t)}</p>
      {error ? (
        <div className="form-message" role="status" aria-live="polite">
          <StatusChip tone="failed" label={t("Not recorded")} />
          <span>{error}</span>
        </div>
      ) : null}
      <div className="row">
        <button type="button" className="btn" disabled={busy} onClick={() => void submit(true)}>
          {busy ? t("Recording…") : t("Record these hours")}
        </button>
        {row.records_presence ? (
          <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => void submit(false)}>
            {t("Take them from the check-in record")}
          </button>
        ) : null}
        <button type="button" className="btn btn-quiet" disabled={busy} onClick={onCancel}>
          {t("Cancel")}
        </button>
      </div>
    </div>
  );
}

function hhmm(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export function Timesheets() {
  const { id = "" } = useParams();
  const { apiBase, me } = useSession();
  const { locale, t, refusal } = useT();
  const maySubmit = me?.authenticated === true && me.permissions.includes("timesheet.submit");
  const [open, setOpen] = useState<string | null>(null);
  const [reasons, setReasons] = useState<Reason[]>([]);
  const [data, setData] = useState<Payload | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  // The read itself, apart from a step's refusal: a limit is calm, a fault can be tried again.
  const [loadFailed, setLoadFailed] = useState<Failure | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await send<{ timesheets?: Payload; error?: { message: string } }>(
      console_.eventTimesheets(apiBase, id),
    );
    if (res.status === 200 && res.body?.timesheets) {
      setData(res.body.timesheets);
      setLoadFailed(null);
    } else {
      setLoadFailed({ status: res.status, refusal: refusal(res.body?.error) });
    }
  }, [apiBase, id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!maySubmit) return;
    send<{ correction_reasons?: Reason[] }>(console_.timesheetCorrectionReasons(apiBase))
      .then((res) => setReasons(res.status === 200 ? (res.body?.correction_reasons ?? []) : []))
      .catch(() => setReasons([]));
  }, [apiBase, maySubmit]);

  const approve = useCallback(
    async (timesheetId: string, step: string) => {
      setBusy(`${timesheetId}:${step}`);
      const res = await send<{ error?: { message: string } }>(
        console_.approveTimesheet(apiBase, timesheetId, step),
      );
      setBusy(null);
      if (res.status >= 400) {
        setFailed(refusal(res.body?.error) ?? t("That step could not be taken."));
        return;
      }
      // RELOADED RATHER THAN PATCHED IN PLACE. The next step, whether the chain is
      // complete and the assignment's own status all move together, and a screen that
      // guessed any of them would be a second copy of `0057`'s rules.
      await load();
    },
    [apiBase, load],
  );

  if (data === null && loadFailed !== null) {
    return (
      <section className="panel">
        <div className="panel-head">
          <h2>{t("Timesheets")}</h2>
        </div>
        <LoadFailed failure={loadFailed} fallback={t("These hours could not be read.")} onRetry={() => void load()} />
      </section>
    );
  }
  if (data === null) {
    return (
      <section className="panel">
        <div className="panel-head">
          <h2>{t("Timesheets")}</h2>
        </div>
        <p className="muted">{t("Reading the hours…")}</p>
      </section>
    );
  }

  return (
    <>
      <section className="panel">
        <div className="panel-head">
          <h2>{t("Timesheets")}</h2>
          <p className="muted">{t("Hours claimed against this event, and where each one is in the chain")}</p>
        </div>
        {failed ? <StatusChip tone="failed" label={failed} /> : null}
        {loadFailed ? <LoadFailed failure={loadFailed} fallback={t("These hours could not be read.")} onRetry={() => void load()} /> : null}
        <div className="stat-tiles">
          <div className="stat-tile">
            <strong className="numeric">{data.summary.shifts}</strong>
            <span>{t("Shifts")}</span>
          </div>
          <div className="stat-tile">
            <strong className="numeric">{data.summary.submitted}</strong>
            <span>{t("Submitted")}</span>
          </div>
          <div className={`stat-tile${Number(data.summary.awaiting) > 0 ? " is-attention" : ""}`}>
            <strong className="numeric">{data.summary.awaiting}</strong>
            <span>{t("Awaiting approval")}</span>
          </div>
          <div className="stat-tile">
            <strong className="numeric">{data.summary.approved}</strong>
            <span>{t("Approved")}</span>
          </div>
          <div className={`stat-tile${Number(data.summary.with_variance) > 0 ? " is-attention" : ""}`}>
            <strong className="numeric">{data.summary.with_variance}</strong>
            <span>{t("With a variance")}</span>
          </div>
        </div>
      </section>

      <section className="panel">
        <div className="panel-head">
          <h2>{t("Hours")}</h2>
          {/* THE CHAIN, NAMED FROM THE SERVER'S OWN ROWS. D9 shortened it to two steps by
              decision; two labels typed here would need a deploy to follow the next one. */}
          <p className="muted">
            {data.steps.map((s) => translateLabel(locale, s.label)).join(t(" then ")) || t("No approval chain is configured")}
          </p>
        </div>
        {data.rows.length === 0 ? (
          <>
            <p className="muted">{t("Nobody is booked on this event yet, so there are no hours to claim.")}</p>
            {/* An empty state names its next step as a button (event.clinic tour, entries 40 and 138). */}
            <Link className="btn btn-secondary" to={`/console/events/${encodeURIComponent(id)}/assignments`}>
              {t("Book the crew in the Control Room")}
            </Link>
          </>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>{t("Who")}</th>
                <th>{t("Post")}</th>
                <th>{t("Worked")}</th>
                <th>{t("Payable")}</th>
                <th>{t("Variance")}</th>
                <th>{t("State")}</th>
                <th>{t("Next step")}</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const s = rowState(r, data.steps.map((x) => ({ ...x, label: translateLabel(locale, x.label) })), t);
                const step = data.steps.find((x) => x.id === r.next_step);
                const key = r.timesheet_id ? `${r.timesheet_id}:${r.next_step}` : "";
                return (
                  <Fragment key={r.assignment_id}>
                  <tr>
                    <td>
                      <strong>{r.worker_name}</strong>
                      {r.is_reserve ? <div className="muted">{t("Reserve")}</div> : null}
                    </td>
                    <td>
                      {r.zone ?? translateLabel(locale, r.role_name)}
                      {r.zone ? <div className="muted">{translateLabel(locale, r.role_name)}</div> : null}
                    </td>
                    <td>
                      {r.actual_start ? `${hhmm(r.actual_start)}–${hhmm(r.actual_end)}` : "—"}
                      {/* WHERE THE HOURS CAME FROM, SAID OUT LOUD. A figure derived from a
                          check-in and one somebody typed carry different evidential weight,
                          and `13-…` §1 says a dispute turns on knowing which. A shift that
                          records no presence says so rather than implying a check happened —
                          which is what V22 must be able to render. */}
                      <div className="muted">
                        {r.timesheet_id === null
                          ? ""
                          : r.hours_from_presence
                            ? t("From the check-in record")
                            : r.records_presence
                              ? t("Stated")
                              : t("Stated — no check-in policy applies")}
                      </div>
                    </td>
                    <td className="numeric">
                      {hm(r.payable_minutes)}
                      {r.break_minutes !== null && r.break_minutes > 0 ? (
                        <div className="muted">
                          {r.break_is_paid
                            ? t("Break {minutes}, paid", { minutes: hm(String(r.break_minutes)) })
                            : t("Break {minutes}, unpaid", { minutes: hm(String(r.break_minutes)) })}
                        </div>
                      ) : null}
                      {r.units_produced !== null ? (
                        <div className="muted">{t("{n} units", { n: r.units_produced })}</div>
                      ) : null}
                    </td>
                    <td className="numeric">
                      {r.variance_flagged ? (
                        <StatusChip
                          tone="attention"
                          label={t("{variance} against {scheduled} scheduled", { variance: hm(r.variance_minutes), scheduled: hm(r.scheduled_minutes) })}
                        />
                      ) : (
                        hm(r.variance_minutes)
                      )}
                      {r.supersedes_id !== null ? (
                        <div className="muted">
                          {t("Correction: {reason}", {
                            reason: translateLabel(locale, reasons.find((x) => x.id === r.correction_reason)?.label ?? r.correction_reason ?? ""),
                          })}
                        </div>
                      ) : null}
                    </td>
                    <td><StatusChip tone={s.tone} label={s.label} /></td>
                    <td>
                      {maySubmit && !r.is_approved ? (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setOpen(open === r.assignment_id ? null : r.assignment_id)}
                        >
                          {r.timesheet_id === null ? t("Record hours") : t("Correct")}
                        </button>
                      ) : null}
                      {r.timesheet_id !== null && step && step.available ? (
                        <button
                          type="button"
                          className="btn"
                          disabled={busy === key}
                          onClick={() => void approve(r.timesheet_id!, step.id)}
                        >
                          {busy === key ? t("Recording…") : translateLabel(locale, step.label)}
                        </button>
                      ) : maySubmit && !r.is_approved ? null : (
                        <span className="muted">{step ? translateLabel(locale, step.label) : "—"}</span>
                      )}
                    </td>
                  </tr>
                  {open === r.assignment_id ? (
                    <tr>
                      <td colSpan={7}>
                        <HoursForm
                          row={r}
                          reasons={reasons}
                          onCancel={() => setOpen(null)}
                          onDone={async () => {
                            setOpen(null);
                            await load();
                          }}
                        />
                      </td>
                    </tr>
                  ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}
